import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import buildReason from './buildReason';
import { HealthBar, StatusCounts } from './StatusBreakdown';
import { subtitle, statusLabel, healthRowsPropType } from './shared';
import {
  AttnHead,
  Cards,
  FCard,
  Bar,
  FStats,
  FActions,
  CardLink,
  HealthyStrip,
  Pill,
  Skeleton,
} from './style';
import { timeAgo } from '../../utils/dates';

const card = (r) => {
  const a = r.attributes || {};
  return (
    <FCard key={r.id} className={a.status}>
      <div className="top">
        <div>
          <div className="nm">{a.name}</div>
          <div className="sub">{subtitle(a)}</div>
        </div>
        <Pill className={a.status}>
          <span className="d" />
          {statusLabel(a.status)}
        </Pill>
      </div>
      <HealthBar attributes={a} />
      <StatusCounts attributes={a} />
      <FStats>
        <span>
          <b>{a.succeeded}</b>/{a.total} {t`succeeded`}
        </span>
        <span>
          {t`last success`}{' '}
          <b>{a.last_success_at ? timeAgo(a.last_success_at) : t`Never`}</b>
        </span>
      </FStats>
      <div className="frs">{buildReason(a)}</div>
      <FActions>
        <CardLink
          href={`/integrations/${r.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t`Open integration ↗`}
        </CardLink>
      </FActions>
    </FCard>
  );
};

const skeletonCards = () =>
  ['a', 'b', 'c'].map((k) => (
    <FCard key={k}>
      <Skeleton style={{ width: '60%' }} />
      <Bar />
      <Skeleton style={{ width: '40%', marginTop: '8px' }} />
    </FCard>
  ));

export default function HealthCards({ rows, loading, emptyState }) {
  if (loading) return <Cards>{skeletonCards()}</Cards>;
  if (!rows.length) return emptyState;

  const attention = rows.filter((r) => r.attributes.status !== 'healthy');
  const healthy = rows.filter((r) => r.attributes.status === 'healthy');

  return (
    <>
      {attention.length > 0 && (
        <>
          <AttnHead>
            {t`Needs attention`}{' '}
            <span className="badge">{attention.length}</span>
          </AttnHead>
          <Cards>{attention.map(card)}</Cards>
        </>
      )}
      {healthy.length > 0 &&
        (attention.length === 0 ? (
          <Cards>{healthy.map(card)}</Cards>
        ) : (
          <HealthyStrip>
            <span>
              ✓ {healthy.length} {t`healthy`}
            </span>
            <div className="chips">
              {healthy.slice(0, 8).map((r) => (
                <span key={r.id} className="hchip">
                  {r.attributes.name}
                </span>
              ))}
              {healthy.length > 8 && (
                <span className="hchip">
                  +{healthy.length - 8} {t`more`}
                </span>
              )}
            </div>
          </HealthyStrip>
        ))}
    </>
  );
}

HealthCards.propTypes = {
  rows: healthRowsPropType.isRequired,
  loading: PropTypes.bool.isRequired,
  emptyState: PropTypes.node.isRequired,
};
